import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import Switch from "../../../../components/common/Switch";
import Button from "../../../../components/common/button";
import DeleteAndroidHome from "../../../../components/tv-app/delete-android-home";
import DiscardSaveButtons from "../../../../components/tv-app/discard-save-buttons";
import {
  Icards,
  ICategoryComponentProps,
  ITableColumn,
} from "../../../../utils/types";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { DndProvider } from "react-dnd";
import useUpdateAndroidTVHomeOrder from "../../../../hooks/use-update-android-home-order";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableRow from "../../../../components/common/DragRow";
import { DragSvg } from "../../../../icons";
import { setDragOnlyInStaging } from "../../../../utils/helpers";
import { useGetUserPermission } from "../../../../context/AuthStaffUserContext";
import AddHotelCategoryDataModal from "../../../../components/hotel/CategoryAddModal";
import EditHotelCategory from "../../../../components/hotel/EditCategoryModal";

interface IFormData {
  //   showMusic: boolean;
  cardsData: Icards[];
}

const FitnessWellness = ({
  data,
  loading,
  onSave,
}: ICategoryComponentProps) => {
  const getInitialFormData = () => {
    return {
      //   showMusic: !!data?.musicSettings?.show,
      cardsData: data?.cards.sort((a, b) => a.orderKey - b?.orderKey),
    };
  };

  const [InitialFormData, setInitialFormData] = useState<IFormData>(
    getInitialFormData()
  );

  const methods = useForm<IFormData>({ defaultValues: InitialFormData });

  const { reset, handleSubmit } = methods;

  const { fields: cardFields, remove: removeMusicField } = useFieldArray({
    name: "cardsData",
    control: methods.control,
    keyName: "customId",
  });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const categoryName = data?.categoryName || "";
  const [cardData, setCardData] = useState(() => ({
    categoryName,
    items:
      [...(cardFields as Icards[])]?.sort((a, b) => a.orderKey - b.orderKey) ??
      [], // Use subData directly or default to an empty array
  }));
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { mutate: updateAndroidTVHomeOrder } = useUpdateAndroidTVHomeOrder();

  useEffect(() => {
    // Sort and update alarmData.items whenever alarmFields changes
    const sorted = [...cardFields].sort((a, b) => a.orderKey - b.orderKey);
    setCardData((prev) => ({
      ...prev,
      items: sorted,
    }));
  }, [cardFields]);

  useEffect(() => {
    if (data?.cards) {
      // Update form values when data changes
      const sortedCards = [...data.cards].sort(
        (a, b) => a.orderKey - b.orderKey
      );
      reset({
        cardsData: sortedCards,
      });

      // Update cardData state
      setCardData({
        categoryName: data.categoryName || "",
        items: sortedCards,
      });

      // Update initial form data reference
      setInitialFormData({
        cardsData: sortedCards,
      });
    }
  }, [data, reset]);

  const getTableDataIndex = (id: string) => {
    return cardFields.findIndex((item) => item.id === id);
  };

  const onDropComplete = () => {
    const updatedItems = [...cardData.items].map((item, index) => ({
      ...item,
      orderKey: index + 1,
    }));

    setCardData((prev) => ({
      ...prev,
      items: updatedItems.sort((a, b) => a?.orderKey - b?.orderKey),
    }));

    const updatedDataWithOrderKey = updatedItems.map((item) => ({
      id: item.id,
      orderKey: item.orderKey,
    }));
    updateAndroidTVHomeOrder({ categoryName, data: updatedDataWithOrderKey });
  };

  const onSubmit = (submitData: IFormData) => {
    const orderKeyMap = new Map(
      cardData.items.map((item) => [item.id, item.orderKey])
    );
    const mergedData = submitData.cardsData.map((item) => ({
      ...item,
      orderKey: orderKeyMap.get(item.id) ?? item.orderKey,
    }));
    onSave({
      categoryName: data?.categoryName,
      data: {
        ...data,
        cards: mergedData.sort((a, b) => a?.orderKey - b?.orderKey),
      },
    });
  };

  const { write: isEditPermission } = useGetUserPermission("tv-app");

  const tableColumns: ITableColumn<Icards>[] = setDragOnlyInStaging<
    ITableColumn<Icards>
  >([
    {
      name: "",
      cell: (Row, index) => (
        <DraggableRow
          index={index}
          moveRow={moveRow}
          onDropComplete={onDropComplete}
        >
          <DragSvg
            height={24}
            width={24}
            className="h-6 w-6 cursor-grab flex-none"
          />
        </DraggableRow>
      ),
      sortable: true,
      width: "10%",
    },
    {
      name: "Image",
      cell: (Row) => (
        <img
          src={Row.image}
          width={64}
          height={39}
          alt={`Image${Row.title}`}
          style={{ maxHeight: "39px", maxWidth: "64px" }}
        />
      ),
      sortable: true,
      width: "20%",
    },
    {
      name: "Name",
      cell: (Row) => Row.title,
      sortable: true,
      width: "20%",
    },
    {
      name: "Description",
      cell: (row) => (
        <div
          className="text-wrap"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "200px",
          }}
        >
          {row.description}
        </div>
      ),
      sortable: true,
      width: "20%",
    },
    {
      name: "Hide/Show",
      cell: (Row) => (
        <Switch name={`cardsData.${getTableDataIndex(Row.id)}.selected`} />
      ),
      sortable: true,
      center: true,
      width: "10%",
    },
    {
      name: "Action",
      cell: (Row) => (
        <div className="flex items-center gap-2">
          <div
            onClick={() => {
              setSelectedId(Row.id);
            }}
          >
            <EditHotelCategory
              title="Edit Fitness-Wellness"
              categoryName={data?.categoryName}
              data={cardFields.find((ele) => ele.id === selectedId) || null}
              isEditVideo={!!cardFields.find((ele) => ele.id === selectedId)}
              subCategoryData={data}
              onSave={onSave}
              onClose={async () => {
                setSelectedId(null);
                // await queryClient.refetchQueries({
                //   queryKey: ["hotel-category"],
                // });
              }}
            />
          </div>
          <DeleteAndroidHome
            categoryName={data?.categoryName}
            deleteId={Row.id}
            disabled={!isEditPermission}
            removeField={() => {
              setInitialFormData((pre) => ({
                ...pre,
                data: pre.cardsData.filter((item) => item.id !== Row.id),
              }));
              removeMusicField(getTableDataIndex(Row.id));
              setCardData({
                ...cardData,
                items: cardData.items.filter((item) => item.id !== Row.id),
              });
            }}
            reLoadData
          />
        </div>
      ),
      sortable: true,
      center: true,
      width: "20%",
    },
  ]);

  const moveRow = (fromIndex: number, toIndex: number) => {
    if (!cardData?.items) return;
    const updatedData = [...cardData.items];
    const [movedItem] = updatedData.splice(fromIndex, 1);
    updatedData.splice(toIndex, 0, movedItem);
    setCardData({
      ...cardData,
      items: updatedData,
    });
  };

  return (
    <FormProvider {...methods}>
      <div>
        <h3 className="text-xl font-semibold text-[#111827]">Fitness + Wellness</h3>
        <div className="">
          {/* <Switch name="showMusic" label="Music (Hide/Show)" /> */}
        </div>
        <div className="flex items-center justify-end w-full mt-4">
          {/* <h4 className="text-base leading-[39px] font-normal text-[#1E2F65]">
            Music List
          </h4> */}
          <Button
            variant="primary"
            className="w-21"
            onClick={openModal}
            disabled={!isEditPermission}
          >
            Add
          </Button>
        </div>
        {/* <div className="h-full max-h-[calc(100vh-440px)] overflow-auto mt-[17px]"> */}
        <DndProvider backend={HTML5Backend}>
          <DataTable
            className="border-t mt-6"
            data={cardData?.items || []}
            columns={tableColumns}
            striped={true}
            keyField="customId"
            persistTableHead
            pagination={cardFields.length > 10}
            customStyles={{
              headRow: { style: { paddingLeft: "36px" } },
              rows: { style: { paddingLeft: "36px" } },
            }}
          />
        </DndProvider>
        {/* </div> */}

        <DiscardSaveButtons
          onDiscard={() => {
            reset(getInitialFormData);
          }}
          onSave={handleSubmit(onSubmit)}
          loading={loading}
          className="mt-10"
        />

        <AddHotelCategoryDataModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          categoryName={data.categoryName}
          modalTitle="Add Fitness-Wellness"
          subCategoryData={data}
          onSave={onSave}
        />
      </div>
    </FormProvider>
  );
};

export default FitnessWellness;
